import type { SkinGachaBannerResponse } from '@kenocyber/shared';
import {
  ALPHA_RESET_WIPED,
  bpsPercentLabel,
  exactOneInLabel,
  RELEASE_COMMON_RULES,
  releaseNoticeFor,
  SKIN_GACHA_DISCLOSURE,
  skinGachaJointMatrix,
  skinGachaLowSocketSentence,
  skinGachaWeightsAreEqual,
} from '@kenocyber/shared';
import { useEffect, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatDecimal } from './format';
import { SkinGachaReveal } from './SkinGachaReveal';
import { balanceAfter, canAfford } from './skin-gacha-machine';
import { useSkinGacha } from './useSkinGacha';

/**
 * The Cash skin roulette, above the wardrobe (gate V24C).
 *
 * ## Everything a player pays with is on the screen before the button
 *
 * The price, the currency by name, the balance now, the balance after, one
 * chance per skin, all six socket chances, all eighteen combinations, the
 * share of copies born with one or two seats, what a duplicate is, what a
 * socket is not, that there is no guarantee by roll count, and that the copy is
 * erased by the alpha reset while purchased Cash comes back. None of it is
 * behind a tooltip, an accordion or a second tab - a probability that needs a
 * gesture to appear is a probability that is hidden.
 *
 * ## Not one number here was invented by the client
 *
 * The price, the weights, the chances, the labels, the duplicate policy, the
 * soulbound flag, the PvP flag, the absence of pity and the release stage all
 * arrive in `GET /api/skins/gacha/banner`. What this file computes is
 * arithmetic ON those - a subtraction for the balance after, a product for the
 * joint table - in `bigint` and in integers, never a float and never a
 * constant. A percentage written down here would be a second source of truth
 * for a number the server already publishes.
 *
 * ## The reset wording is rendered, not written
 *
 * `store/alpha-terms.ts` owns the exact paragraphs each stage promises, and
 * says in as many words that an interface composes no sentence of its own about
 * a reset or a recredit. The banner tells this screen WHICH stage; the strings
 * come from there.
 *
 * ## Nothing on this screen is optimistic
 *
 * A confirmation charges; the answer decides. There is no path that draws a
 * copy before the server produced one, no client randomness anywhere, and no
 * animation that begins before an authoritative result exists.
 */

interface SkinGachaBannerProps {
  /**
   * Resolves a skin's PNG, or answers null.
   *
   * A prop rather than a hook of this component's own, so the sidecar is
   * fetched once for the whole inventory and this stays a pure function of what
   * it is given. A skin's picture is declared in `game-assets/skin-manifest.json`,
   * not in the main asset manifest - see `useSkinArt`.
   */
  readonly artFor: (definitionId: string) => string | null;
  /**
   * Told the instance id of a confirmed roll, so the shelf can be re-read.
   *
   * Never told about a refusal, a conflict or a dropped connection: there is
   * no confirmed state to hand anybody, and a wardrobe that re-read on a
   * failure would be asking the server the same question twice for nothing.
   */
  readonly onRolled: (instanceId: string) => void;
  /** Focuses the wardrobe list after a reveal. Looking, never equipping. */
  readonly onOpenWardrobe: () => void;
}

/**
 * Mounted only while the wardrobe tab is open.
 *
 * That is what makes "one read per open" true without a flag: the banner is
 * fetched by the hook's own mount effect, and switching tabs unmounts the whole
 * thing - including any half-open confirmation, which is the behaviour a player
 * expects from a screen they left.
 */
export function SkinGachaBanner({ artFor, onRolled, onOpenWardrobe }: SkinGachaBannerProps) {
  const gacha = useSkinGacha({ onRolled });
  const { state } = gacha;
  /** The button that opened the odds. Focus comes back to it on close. */
  const trigger = useRef<HTMLButtonElement | null>(null);

  if (state.phase === 'loading') {
    return (
      <section className="skin-gacha" data-testid="skin-gacha" data-phase="loading">
        <p className="skin-gacha-state">Carregando a roleta de skins…</p>
      </section>
    );
  }

  if (!state.banner) {
    /*
     * Two different things, told apart.
     *
     * "There is no banner" is the shipped build's permanent and correct answer,
     * and drawing a red box for it would put an error on a screen behaving
     * exactly as designed. "The read broke" is a failure, says so, and offers
     * the retry the other one has no use for.
     */
    const broke = state.phase === 'failed';
    return (
      <section
        className="skin-gacha"
        data-testid="skin-gacha"
        data-phase={broke ? 'failed' : 'unavailable'}
      >
        {broke ? (
          <p className="skin-error" role="alert" data-testid="skin-gacha-error">
            {state.message}
          </p>
        ) : (
          <p className="skin-gacha-state" data-testid="skin-gacha-unavailable">
            A roleta de skins não está disponível no momento.
          </p>
        )}
      </section>
    );
  }

  const banner = state.banner;
  return (
    <section className="skin-gacha" data-testid="skin-gacha" data-phase={state.phase}>
      <BannerCard banner={banner} gacha={gacha} triggerRef={trigger} />
      {/*
        Kept mounted while the roll is in flight.
        Unmounting the panel at the instant a player confirms would take the
        price, the odds and the confirmation off the screen and leave them
        looking at nothing at all until the answer arrived.
      */}
      {state.phase === 'details' || state.phase === 'confirming' || state.phase === 'submitting' ? (
        <Disclosure banner={banner} gacha={gacha} artFor={artFor} triggerRef={trigger} />
      ) : null}
      {state.result ? (
        <SkinGachaReveal
          artFor={artFor}
          result={state.result}
          onFinished={gacha.revealFinished}
          onClose={() => {
            gacha.dismiss();
            trigger.current?.focus();
          }}
          onOpenWardrobe={() => {
            /*
             * Two dispatches, in order.
             *
             * `dismiss` lets the result go and puts the odds back on screen -
             * which is where a player who wants to roll again belongs.
             * Somebody heading for the shelf wants the opposite, so the panel
             * is closed behind them and the whole tab is the wardrobe.
             */
            gacha.dismiss();
            gacha.closeDetails();
            onOpenWardrobe();
          }}
        />
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  The card                                                                  */
/* -------------------------------------------------------------------------- */

function BannerCard({
  banner,
  gacha,
  triggerRef,
}: {
  banner: SkinGachaBannerResponse;
  gacha: ReturnType<typeof useSkinGacha>;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
}) {
  const { state } = gacha;
  const after = balanceAfter(banner);
  const affordable = canAfford(banner);

  return (
    <div className="skin-gacha-card">
      <div className="skin-gacha-head">
        <h4 className="skin-gacha-title">ROLETA DE SKINS</h4>
        <span className="skin-gacha-tag" data-testid="skin-gacha-alpha-tag">
          {SKIN_GACHA_DISCLOSURE.alphaPrice}
        </span>
      </div>

      <dl className="skin-gacha-figures">
        <div>
          <dt>Custo por rolagem</dt>
          {/* The currency by NAME, never an icon on its own. */}
          <dd data-testid="skin-gacha-price">{formatDecimal(banner.priceCash)} Cash</dd>
        </div>
        <div>
          <dt>Seu saldo</dt>
          <dd data-testid="skin-gacha-balance">{formatDecimal(banner.balance)} Cash</dd>
        </div>
        <div>
          <dt>Saldo depois</dt>
          <dd data-testid="skin-gacha-balance-after">
            {after === null
              ? 'Não cobre esta rolagem'
              : `${formatDecimal(after.toString(10))} Cash`}
          </dd>
        </div>
      </dl>

      {/* Said BEFORE the attempt, never after it. */}
      {affordable ? null : (
        <p className="skin-gacha-warning" data-testid="skin-gacha-short">
          Seu saldo de Cash não cobre esta rolagem.
        </p>
      )}

      <p className="skin-gacha-summary">{SKIN_GACHA_DISCLOSURE.noPity}</p>

      <div className="skin-gacha-actions">
        <button
          type="button"
          className="skin-button skin-button-primary"
          data-testid="skin-gacha-open-details"
          ref={triggerRef}
          onClick={gacha.openDetails}
        >
          VER PREÇO E CHANCES
        </button>
      </div>

      {/* Every terminal state of an attempt, on the card rather than in a
          modal a player has already closed. */}
      {state.phase === 'insufficient' ? (
        <p className="skin-gacha-warning" role="alert" data-testid="skin-gacha-insufficient">
          {state.message}
        </p>
      ) : null}
      {state.phase === 'failed' && state.message ? (
        <p className="skin-error" role="alert" data-testid="skin-gacha-failed">
          {state.message}
        </p>
      ) : null}
      {state.phase === 'uncertain' ? (
        <div className="skin-gacha-uncertain" role="alert" data-testid="skin-gacha-uncertain">
          <p>{state.message}</p>
          <button
            type="button"
            className="skin-button"
            data-testid="skin-gacha-retry"
            onClick={gacha.retry}
          >
            TENTAR NOVAMENTE
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The disclosure                                                            */
/* -------------------------------------------------------------------------- */

function Disclosure({
  banner,
  gacha,
  artFor,
  triggerRef,
}: {
  banner: SkinGachaBannerResponse;
  gacha: ReturnType<typeof useSkinGacha>;
  artFor: (definitionId: string) => string | null;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
}) {
  const { state } = gacha;
  const poolTotal = banner.pool.reduce((sum, entry) => sum + entry.weight, 0);
  const equal = skinGachaWeightsAreEqual(banner.pool);
  const matrix = useMemo(
    () => skinGachaJointMatrix(banner.pool, banner.socketOdds),
    [banner.pool, banner.socketOdds],
  );
  /** Ties the matrix to the sentence that explains its identical rows. */
  const uniformNoteId = useId();
  const notice = releaseNoticeFor(banner.releaseStage);
  const after = balanceAfter(banner);
  const affordable = canAfford(banner);
  const close = () => {
    gacha.closeDetails();
    triggerRef.current?.focus();
  };

  return (
    <div className="skin-gacha-disclosure" data-testid="skin-gacha-details">
      <div className="skin-gacha-disclosure-head">
        <h4>O que custa, e o que pode sair</h4>
        <button
          type="button"
          className="overview-close"
          data-testid="skin-gacha-close-details"
          aria-label="Fechar chances"
          onClick={close}
        >
          ×
        </button>
      </div>

      {/* 1. The money, again, where the decision is made. */}
      <section className="skin-gacha-block">
        <h5>Custo</h5>
        <ul className="skin-gacha-facts">
          <li>
            Preço: <strong>{formatDecimal(banner.priceCash)} Cash</strong> por rolagem.
          </li>
          <li>
            Saldo atual: <strong>{formatDecimal(banner.balance)} Cash</strong>.
          </li>
          <li data-testid="skin-gacha-detail-after">
            Saldo depois desta rolagem:{' '}
            <strong>
              {after === null
                ? 'seu saldo não cobre esta rolagem'
                : `${formatDecimal(after.toString(10))} Cash`}
            </strong>
            .
          </li>
        </ul>
      </section>

      {/* 2. The three skins, exact first and decimal second. */}
      <section className="skin-gacha-block">
        <h5>As skins que podem sair</h5>
        <ul className="skin-gacha-pool" data-testid="skin-gacha-pool">
          {banner.pool.map((entry) => {
            const art = artFor(entry.definitionId);
            const oneIn = exactOneInLabel(entry.weight, poolTotal);
            return (
              <li key={entry.definitionId} data-testid={`skin-gacha-pool-${entry.definitionId}`}>
                <span className="skin-gacha-pool-art">
                  {art ? <img src={art} alt="" draggable={false} decoding="async" /> : null}
                </span>
                <span className="skin-gacha-pool-body">
                  <span className="skin-gacha-pool-name">{entry.displayName}</span>
                  {entry.blurb ? <span className="market-row-sub">{entry.blurb}</span> : null}
                </span>
                <span className="skin-gacha-pool-chance">
                  {/* The exact statement leads; the decimal follows it. */}
                  {oneIn ? <strong>{oneIn}</strong> : null}
                  <span data-testid={`skin-gacha-chance-${entry.definitionId}`}>
                    {bpsPercentLabel(entry.chanceBps)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
        {equal ? (
          <p className="skin-gacha-note" data-testid="skin-gacha-rounding">
            {SKIN_GACHA_DISCLOSURE.rounding}
          </p>
        ) : null}
      </section>

      {/* 3. All six capacities, always. */}
      <section className="skin-gacha-block">
        <h5>Quantidade de sockets</h5>
        <table className="skin-gacha-table" data-testid="skin-gacha-socket-table">
          <thead>
            <tr>
              <th scope="col">Sockets</th>
              <th scope="col">Chance</th>
            </tr>
          </thead>
          <tbody>
            {banner.socketOdds.map((entry) => (
              <tr key={entry.sockets} data-testid={`skin-gacha-socket-${entry.sockets}`}>
                <th scope="row">{entry.sockets}</th>
                <td>{entry.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="skin-gacha-note" data-testid="skin-gacha-low-sockets">
          {skinGachaLowSocketSentence(banner.socketOdds)}
        </p>
        <p className="skin-gacha-note">{SKIN_GACHA_DISCLOSURE.independence}</p>
      </section>

      {/*
        4. The eighteen combinations, as a matrix, visible without a click.

        Six rows of capacity by three columns of skin. The same eighteen numbers
        the vertical list carried - none removed, none summarised - in a third of
        the height, which is what lets the reset, the recredit and the two
        buttons come back up the page instead of sitting under a scrolling box.
      */}
      <section className="skin-gacha-block">
        <h5>Chance de cada resultado</h5>
        <table
          className="skin-gacha-matrix"
          data-testid="skin-gacha-joint-table"
          aria-describedby={uniformNoteId}
        >
          <caption className="skin-gacha-caption">
            Cada célula é a chance de sair aquela skin já com aquela quantidade de sockets.
          </caption>
          <thead>
            <tr>
              <th scope="col">Sockets</th>
              {matrix.skins.map((skin) => {
                const art = artFor(skin.definitionId);
                return (
                  <th scope="col" key={skin.definitionId}>
                    {/*
                      The thumbnail is decoration beside the name, never instead
                      of it: `alt=""` keeps a screen reader on the words, and the
                      words are what address the cells below.
                    */}
                    <span className="skin-gacha-matrix-head">
                      {art ? <img src={art} alt="" draggable={false} decoding="async" /> : null}
                      <span>{skin.displayName}</span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.sockets}>
                <th scope="row">{row.sockets}</th>
                {row.cells.map((cell) => (
                  <td
                    key={cell.definitionId}
                    data-testid={`skin-gacha-joint-${cell.definitionId}-${row.sockets}`}
                  >
                    {bpsPercentLabel(cell.chanceBps)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {matrix.rowsAreUniform ? (
          <p className="skin-gacha-note" id={uniformNoteId} data-testid="skin-gacha-joint-uniform">
            As três colunas de uma mesma linha são iguais porque cada skin é 1 em 3 e a quantidade
            de sockets é sorteada separadamente: a capacidade não depende de qual skin saiu.
          </p>
        ) : null}
      </section>

      {/* 5. What the copy is, and what it is not. */}
      <section className="skin-gacha-block">
        <h5>O que você recebe</h5>
        <ul className="skin-gacha-facts" data-testid="skin-gacha-terms">
          <li>{SKIN_GACHA_DISCLOSURE.duplicates}</li>
          <li>{SKIN_GACHA_DISCLOSURE.emptySockets}</li>
          <li>{SKIN_GACHA_DISCLOSURE.noAdvantage}</li>
          <li>{SKIN_GACHA_DISCLOSURE.soulbound}</li>
          <li>{SKIN_GACHA_DISCLOSURE.noPity}</li>
        </ul>
      </section>

      {/* 6. The reset and the recredit, in the stage's own words. */}
      <section className="skin-gacha-block">
        <h5>Reset e recrédito</h5>
        {notice.resetsAtEnd ? (
          <p className="skin-gacha-warning" data-testid="skin-gacha-reset">
            {/* The line that names skins, taken from the authoritative list
                rather than paraphrased into a sentence of this screen's own. */}
            {ALPHA_RESET_WIPED.includes('Pets e skins')
              ? 'A skin sorteada será APAGADA no reset desta fase, como todo o resto do progresso.'
              : notice.summary}
          </p>
        ) : null}
        <ul className="skin-gacha-facts" data-testid="skin-gacha-release">
          {notice.paragraphs.map((paragraph) => (
            <li key={paragraph}>{paragraph}</li>
          ))}
        </ul>
        <details className="skin-gacha-rules">
          <summary>Regras completas do recrédito</summary>
          <ul className="skin-gacha-facts">
            {RELEASE_COMMON_RULES.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
          {notice.resetsAtEnd ? (
            <>
              <p className="skin-gacha-note">O reset apaga:</p>
              <ul className="skin-gacha-facts" data-testid="skin-gacha-wiped">
                {ALPHA_RESET_WIPED.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </>
          ) : null}
        </details>
      </section>

      {/* 7. The step that spends. */}
      <div className="skin-gacha-disclosure-actions">
        {/*
          The same weight as the button beside it, deliberately.
          `ghost-button` renders very dim against this panel, and a screen where
          "leave without buying" recedes while "spend" glows is asymmetric in
          exactly the direction a transparency screen must not be. Only the
          primary tint distinguishes them.
        */}
        <button
          type="button"
          className="skin-button"
          data-testid="skin-gacha-cancel"
          onClick={close}
        >
          FECHAR SEM COMPRAR
        </button>
        <button
          type="button"
          className="skin-button skin-button-primary"
          data-testid="skin-gacha-roll"
          disabled={!affordable || state.phase !== 'details'}
          onClick={gacha.requestConfirmation}
        >
          ROLAR POR {formatDecimal(banner.priceCash)} CASH
        </button>
      </div>

      {state.phase === 'confirming' || state.phase === 'submitting' ? (
        <Confirmation banner={banner} gacha={gacha} />
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The confirmation                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The step that spends, as a modal of its own.
 *
 * ## Why a portal, and not the marketplace's `.market-confirm`
 *
 * It borrowed that class first, and the screenshots showed the result: the rule
 * is `position: absolute; inset: 0`, so it covers whatever the nearest
 * POSITIONED ancestor happens to be. Inside the marketplace that is the
 * marketplace's own card; inside the inventory it was something else entirely,
 * and the sentences ended up floating over the odds with no panel behind them.
 * A confirmation nobody can read is not a confirmation.
 *
 * So it is a portal with its own backdrop, exactly like the reveal - which
 * needed one for the same family of reason - and it carries the same
 * `data-inventory-portal` marker, without which the panel's outside-click rule
 * would close the inventory the moment a player pressed `CONFIRMAR`.
 *
 * ## Escape cancels, and does not close the inventory behind it
 *
 * The panel listens for Escape on `window`; this listens in the CAPTURE phase
 * and stops propagation, so the key means the nearest thing it could mean.
 */
function Confirmation({
  banner,
  gacha,
}: {
  banner: SkinGachaBannerResponse;
  gacha: ReturnType<typeof useSkinGacha>;
}) {
  const after = balanceAfter(banner);
  const charging = gacha.state.phase === 'submitting';
  const dialog = useRef<HTMLDivElement | null>(null);
  const cancel = gacha.cancelConfirmation;

  useEffect(() => {
    dialog.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.stopPropagation();
      // Never mid-charge: an answer is still owed to a request that left.
      if (!charging) {
        cancel();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [cancel, charging]);

  return createPortal(
    <div className="skin-gacha-confirm-backdrop" data-inventory-portal="skin-gacha-confirm">
      {/*
        `tabIndex={-1}` makes the dialog programmatically focusable without
        putting it in the tab order, which is how focus is moved into a modal.
      */}
      <div
        className="skin-gacha-confirm"
        role="dialog"
        aria-modal="true"
        aria-label="Confirmar rolagem"
        data-testid="skin-gacha-confirm"
        ref={dialog}
        tabIndex={-1}
      >
        <p className="skin-gacha-confirm-question">
          Rolar uma vez por{' '}
          <strong data-testid="skin-gacha-confirm-price">
            {formatDecimal(banner.priceCash)} Cash
          </strong>
          ?
        </p>
        <p data-testid="skin-gacha-confirm-after">
          Seu saldo passa de <strong>{formatDecimal(banner.balance)} Cash</strong> para{' '}
          <strong>{after === null ? '—' : `${formatDecimal(after.toString(10))} Cash`}</strong>.
        </p>
        <p className="skin-gacha-confirm-warning">{SKIN_GACHA_DISCLOSURE.confirmation}</p>
        <div className="skin-gacha-confirm-actions">
          <button
            type="button"
            className="skin-button"
            data-testid="skin-gacha-confirm-cancel"
            disabled={charging}
            onClick={cancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="skin-button skin-button-primary"
            data-testid="skin-gacha-confirm-roll"
            // Disabled the moment the request leaves, and the machine refuses a
            // second `submit` anyway: a double click is one economic operation.
            disabled={charging}
            onClick={gacha.confirm}
          >
            {charging ? 'ROLANDO…' : 'CONFIRMAR'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
